// src/store/ictRelatedSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiClient from "./apiClient";

// Helper: normalize API response into an array
const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.items)) return data.items;
  // if single object, wrap it
  if (data && typeof data === "object") return [data];
  return [];
};

// FETCH: all related ICT records for a given ICT
export const fetchRelatedByIctId = createAsyncThunk(
  "ictRelated/fetchByIctId",
  async (ictId, { rejectWithValue }) => {
    try {
      const res = await apiClient.get(`/related-ict/ictId/${ictId}`);
      console.log("👉 fetchRelatedByIctId response", ictId, res.data); // 🔍 add this
      const data = normalizeList(res.data);
      return data;
    } catch (err) {
      console.error("❌ fetchRelatedByIctId error", err);
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// INSERT: create new relation
// expected payload: { ictId, relatedIctId, status: "A" }
export const insertRelated = createAsyncThunk(
  "ictRelated/insertRelated",
  async (data, { rejectWithValue }) => {
    try {
      const res = await apiClient.post("/related-ict/", data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// UPDATE: update relation by id
// payload: { id, ...rest }
export const updateRelated = createAsyncThunk(
  "ictRelated/updateRelated",
  async ({ id, ...data }, { rejectWithValue }) => {
    try {
      const res = await apiClient.put(`/related-ict/${id}`, data);
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// PATCH STATUS: toggle A/D
// payload: { id, status }
export const patchRelatedStatus = createAsyncThunk(
  "ictRelated/patchRelatedStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const res = await apiClient.patch(`/related-ict/${id}/status`, {
        status,
      });
      return res.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// DELETE: delete relation by id
export const deleteRelated = createAsyncThunk(
  "ictRelated/deleteRelated",
  async (id, { rejectWithValue }) => {
    try {
      const res = await apiClient.delete(`/related-ict/${id}`);
      return res.data; // could be deleted object or list
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const ictRelatedSlice = createSlice({
  name: "ictRelated",
  initialState: {
    relatedIcts: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // FETCH
      .addCase(fetchRelatedByIctId.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRelatedByIctId.fulfilled, (state, action) => {
        state.loading = false;
        state.relatedIcts = normalizeList(action.payload);
      })
      .addCase(fetchRelatedByIctId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.relatedIcts = [];
      })

      // INSERT
      .addCase(insertRelated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(insertRelated.fulfilled, (state, action) => {
        state.loading = false;
        const list = normalizeList(action.payload);
        // if backend returns full list, replace; else push single
        if (list.length > 1) {
          state.relatedIcts = list;
        } else if (list.length === 1) {
          state.relatedIcts.push(list[0]);
        }
      })
      .addCase(insertRelated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE
      .addCase(updateRelated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateRelated.fulfilled, (state, action) => {
        state.loading = false;
        const updated = Array.isArray(action.payload)
          ? action.payload[0]
          : action.payload;
        const idx = state.relatedIcts.findIndex((r) => r.id === updated.id);
        if (idx !== -1) state.relatedIcts[idx] = updated;
      })
      .addCase(updateRelated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // PATCH STATUS
      .addCase(patchRelatedStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(patchRelatedStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updated = Array.isArray(action.payload)
          ? action.payload[0]
          : action.payload;
        const idx = state.relatedIcts.findIndex((r) => r.id === updated.id);
        if (idx !== -1) {
          state.relatedIcts[idx] = updated;
        }
      })
      .addCase(patchRelatedStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE
      .addCase(deleteRelated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteRelated.fulfilled, (state, action) => {
        state.loading = false;
        const list = normalizeList(action.payload);
        if (list.length > 0 && list[0].id) {
          // assume backend sent deleted object(s)
          const deletedIds = list.map((x) => x.id);
          state.relatedIcts = state.relatedIcts.filter(
            (r) => !deletedIds.includes(r.id)
          );
        } else {
          // fallback: filter by arg
          const deletedId = action.meta.arg;
          state.relatedIcts = state.relatedIcts.filter(
            (r) => r.id !== deletedId
          );
        }
      })
      .addCase(deleteRelated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export default ictRelatedSlice.reducer;
